'use client'

import { useEffect, useRef, useState, type RefObject } from 'react'

export function useStoryOpening(sectionRef: RefObject<HTMLElement | null>, enabled: boolean) {
  const [playing, setPlaying] = useState(false)
  const [prominentHint, setProminentHint] = useState(false)
  const attempted = useRef(false)

  useEffect(() => {
    const section = sectionRef.current
    if (!enabled || !section || attempted.current || (location.hash && !['#top', '#history'].includes(location.hash))) return

    let raf = 0
    let stopped = false
    let startedAt: number | null = null
    const startY = section.getBoundingClientRect().top + window.scrollY
    setProminentHint(false)
    setPlaying(true)
    const finish = () => {
      stopped = true
      cancelAnimationFrame(raf)
      setPlaying(false)
    }
    const takeControl = () => {
      setProminentHint(false)
      finish()
    }
    const onPointerDown = (event: Event) => {
      // Keep navigation in place between pointerdown and click, including the mobile menu.
      if (event.target instanceof Element && event.target.closest('header')) {
        finish()
        setProminentHint(true)
      } else takeControl()
    }
    const onKey = (event: KeyboardEvent) => {
      if (['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' ', 'Tab', 'Escape'].includes(event.key)) takeControl()
    }
    const onVisibility = () => { if (document.hidden) finish() }
    const tick = (now: number) => {
      if (stopped) return
      if (startedAt === null) {
        startedAt = now
        setPlaying(true)
        attempted.current = true
      }
      const t = Math.min(1, (now - startedAt) / 1000)
      const eased = t * t * (3 - 2 * t)
      const distance = Math.max(0, section.offsetHeight - window.innerHeight) * .108
      // Advance the real scroll position so manual input inherits the same timeline.
      window.scrollTo({ top: startY + distance * eased, behavior: 'instant' })
      if (t < 1) raf = requestAnimationFrame(tick)
      else {
        finish()
        setProminentHint(true)
      }
    }
    window.addEventListener('wheel', takeControl, { passive: true })
    window.addEventListener('touchstart', onPointerDown, { passive: true })
    window.addEventListener('touchmove', takeControl, { passive: true })
    window.addEventListener('pointerdown', onPointerDown, { passive: true })
    window.addEventListener('keydown', onKey)
    window.addEventListener('resize', finish)
    document.addEventListener('visibilitychange', onVisibility)
    raf = requestAnimationFrame(tick)
    return () => {
      stopped = true
      cancelAnimationFrame(raf)
      setPlaying(false)
      setProminentHint(false)
      window.removeEventListener('wheel', takeControl)
      window.removeEventListener('touchstart', onPointerDown)
      window.removeEventListener('touchmove', takeControl)
      window.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', finish)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [enabled, sectionRef])

  return { playing, prominentHint }
}
